import cuid from 'cuid';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { AncillaryService } from '@apis/ancillaryServices';

import MoreIcon from '@icons/new/more.svg';

import { useViewportSize } from '@hooks/useViewportSize';

import { getTranslationsState } from '@redux/selectors';

import styles from './AncillaryServices.module.scss';

interface AncillaryServicesProps {
  ancillaries: AncillaryService[];
  onAncillaryClick: (namedKey: string, genzoActionId: string) => void;
}

const MAX_VISIBLE_ITEMS = 5;
const MAX_VISIBLE_ITEMS_TABLET = 4;

const AncillaryServices: React.FC<AncillaryServicesProps> = ({ ancillaries, onAncillaryClick }) => {
  const [isMoreDropdownOpen, setIsMoreDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const moreButtonRef = useRef<HTMLButtonElement>(null);
  const translations = useSelector(getTranslationsState);

  const { width } = useViewportSize();
  const isTablet = width < 1024;

  // Adjust max visible items based on screen size
  const maxVisibleItems = isTablet ? MAX_VISIBLE_ITEMS_TABLET : MAX_VISIBLE_ITEMS;

  const visibleAncillaries = ancillaries.slice(0, maxVisibleItems - 1);
  const moreAncillaries = ancillaries.slice(maxVisibleItems - 1);
  const showMoreButton = ancillaries.length > maxVisibleItems;

  const handleMoreClick = useCallback(() => {
    setIsMoreDropdownOpen((prev) => !prev);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        moreButtonRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        !moreButtonRef.current.contains(event.target as Node)
      ) {
        setIsMoreDropdownOpen(false);
      }
    };

    if (isMoreDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isMoreDropdownOpen]);

  const renderAncillaryItem = (ancillary: AncillaryService, isInDropdown = false) => {
    const genzoActionId = cuid();

    return (
      <a
        key={`${ancillary.named_key}-${isInDropdown ? 'dropdown' : 'main'}`}
        className={isInDropdown ? styles.dropdownItem : styles.ancillaryService}
        href={ancillary.deeplink}
        target='_blank'
        rel='noreferrer nofollow'
        onClick={() => onAncillaryClick(ancillary.named_key, genzoActionId)}
      >
        <img
          src={ancillary.homepage_icon || ''}
          alt={ancillary.homepage_title}
          className={styles.ancillaryIcon}
        />
        <span className={styles.ancillaryText}>{ancillary.homepage_title}</span>
      </a>
    );
  };

  return (
    <div className={styles.ancillaryContainer}>
      <div className={styles.subContainer}>
        {/* Render visible ancillaries */}
        {(showMoreButton ? visibleAncillaries : ancillaries).map((ancillary) =>
          renderAncillaryItem(ancillary),
        )}

        {/* Render More button if needed */}
        {showMoreButton && (
          <div className={styles.moreContainer}>
            <button
              ref={moreButtonRef}
              className={styles.moreButton}
              onClick={handleMoreClick}
              aria-label='More ancillaries'
            >
              <MoreIcon className={styles.ancillaryIcon} />
              <span className={styles.ancillaryText}>{translations.more}</span>
            </button>

            {/* Dropdown */}
            {isMoreDropdownOpen && (
              <div ref={dropdownRef} className={styles.dropdown}>
                {moreAncillaries.map((ancillary) => renderAncillaryItem(ancillary, true))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AncillaryServices;
